/**
 * RefineVoice Editor - TipTap-based Editor for Hybrid Voice + Keyboard Input
 *
 * This component replaces the contentEditable div with a proper rich text editor
 * that handles cursor positioning, text selection, and hybrid input seamlessly.
 */

import { EditorContent } from '@tiptap/react';
import { useCallback, useEffect, useRef } from 'react';

interface RefineVoiceEditorProps {
  content: string;
  widgetId: string;
  interimContent?: string;
  className?: string;
  onTouchStart?: (event: React.TouchEvent) => void;
  onTouchMove?: (event: React.TouchEvent) => void;
  onTouchEnd?: (event: React.TouchEvent) => void;
  editorRef?: React.MutableRefObject<any>;
  ariaLabel?: string;
  screenReaderAnnouncement?: string;
  onFocusChange?: (isFocused: boolean) => void;
  editor?: any; // TipTap editor instance from context
}

export function RefineVoiceEditor({
  content,
  widgetId,
  interimContent = '',
  className = '',
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  editorRef: externalEditorRef,
  ariaLabel,
  screenReaderAnnouncement = '',
  onFocusChange,
  editor,
}: RefineVoiceEditorProps) {
  const internalEditorRef = useRef<any>(null);

  internalEditorRef.current = editor;

  // Insert text at current cursor position
  const insertText = useCallback(
    (text: string, _source: string) => {
      if (!editor) return;

      editor
        .chain()
        .focus()
        .insertContent(text + ' ')
        .run();

      // Update input mode tracking
      // TODO: Add text segment tracking for hybrid input
    },
    [editor]
  );

  // Replace text range
  const replaceRange = useCallback(
    (from: number, to: number, text: string, _source: string) => {
      if (!editor) return;

      // insertContentAt replaces the range and leaves the cursor after the
      // inserted text. A separate deleteRange + insertContent would insert
      // at the *current* selection, not at `from`.
      editor.chain().focus().insertContentAt({ from, to }, text).run();
    },
    [editor]
  );

  // Get current cursor position
  const getCursorPosition = useCallback(() => {
    if (!editor) return 0;
    return editor.state.selection.from;
  }, [editor]);

  // Set cursor position
  const setCursorPosition = useCallback(
    (position: number) => {
      if (!editor) return;
      editor.chain().focus().setTextSelection(position).run();
    },
    [editor]
  );

  // Set interim transcription text
  const setInterimText = useCallback(
    (text: string) => {
      if (!editor) return;
      editor.commands.setInterim(text);
    },
    [editor]
  );

  // Clear interim text
  const clearInterimText = useCallback(() => {
    if (!editor) return;
    editor.commands.clearInterim();
  }, [editor]);

  // Set word-level data with alternatives
  const setWordData = useCallback(
    (words: any[]) => {
      if (!editor) return;
      editor.commands.setWordData(words);
    },
    [editor]
  );

  // Update a specific word
  const updateWord = useCallback(
    (index: number, newWord: string) => {
      if (!editor) return;
      editor.commands.updateWord(index, newWord);
    },
    [editor]
  );

  // Expose editor methods via ref for parent components
  useEffect(() => {
    if (editor && internalEditorRef.current) {
      // Store methods on the editor instance for voice integration
      (editor as any).insertText = insertText;
      (editor as any).replaceRange = replaceRange;
      (editor as any).getCursorPosition = getCursorPosition;
      (editor as any).setCursorPosition = setCursorPosition;

      // Add extension-based methods
      (editor as any).setInterimText = setInterimText;
      (editor as any).clearInterimText = clearInterimText;
      (editor as any).setWordData = setWordData;
      (editor as any).updateWord = updateWord;

      // Expose via external ref if provided
      if (externalEditorRef) {
        externalEditorRef.current = editor;
      }

      // DOM anti-pattern removed - use EditorContext instead
    }
  }, [
    editor,
    insertText,
    replaceRange,
    getCursorPosition,
    setCursorPosition,
    setInterimText,
    clearInterimText,
    setWordData,
    updateWord,
    externalEditorRef,
  ]);

  // Update content when prop changes (but don't disrupt cursor)
  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.getText();
    if (currentContent !== content) {
      // Only update if the content is actually different
      // and preserve cursor position
      const currentPos = editor.state.selection.from;
      editor.commands.setContent(content, { emitUpdate: false });
      editor.chain().focus().setTextSelection(currentPos).run();
    }
  }, [content, editor]);

  // Handle interim content updates
  useEffect(() => {
    if (!editor) return;

    if (interimContent) {
      editor.commands.setInterim(interimContent);
    } else {
      editor.commands.clearInterim();
    }
  }, [interimContent, editor]);

  // Cleanup
  useEffect(() => {
    return () => {
      // The editor instance is created and owned by the parent (via
      // useEditor); this component only borrows it, so it must clear its
      // ref but must NOT destroy the editor — that is the owner's job.
      if (externalEditorRef) {
        externalEditorRef.current = null;
      }
    };
  }, [externalEditorRef]);

  // Handle focus changes
  const handleFocus = useCallback(() => {
    onFocusChange?.(true);
  }, [onFocusChange]);

  const handleBlur = useCallback(() => {
    onFocusChange?.(false);
  }, [onFocusChange]);

  // Simple class name without input mode styling
  const editorClassName = `refinevoice-editor refinevoice-widget-${widgetId} ${className}`.trim();

  return (
    <section
      className={editorClassName}
      aria-label={ariaLabel || `RefineVoice Editor ${widgetId}`}
      aria-describedby={`refinevoice-description-${widgetId}`}
      tabIndex={-1}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Screen reader description */}
      <div id={`refinevoice-description-${widgetId}`} className="sr-only">
        Voice and text editor. Press spacebar to start recording, Control+Enter to refine text,
        Control+K to clear.
      </div>

      {/* Live region for announcements */}
      {/* biome-ignore lint/a11y/useSemanticElements: ARIA live region requires div with role=status */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {screenReaderAnnouncement}
      </div>

      <EditorContent editor={editor} />
      <style>{`
        .refinevoice-editor .ProseMirror {
          outline: none;
          padding: 24px;
          padding-right: 80px; /* Space for FAB button */
          margin: 0;
          min-height: 200px;
        }

        .refinevoice-editor .ProseMirror p {
          margin: 0;
        }

        .refinevoice-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          pointer-events: none;
          height: 0;
          opacity: 0.5;
          font-style: italic;
        }

        /* Interim text styling */
        .refinevoice-editor .interim-text {
          opacity: 0.5;
          font-style: italic;
        }

        /* Word alternatives styling */
        .refinevoice-editor .word-with-alternatives {
          cursor: pointer;
          transition: background-color 150ms;
        }

        .refinevoice-editor .word-with-alternatives:hover {
          background-color: rgba(0, 123, 255, 0.08);
        }

        .refinevoice-editor .word-with-alternatives.confidence-medium {
          border-bottom: 2px dotted #f59e0b;
        }

        .refinevoice-editor .word-with-alternatives.confidence-low {
          border-bottom: 2px dotted #ef4444;
        }
      `}</style>
    </section>
  );
}
